import { CHAT_HISTORY_API_BASE as CHAT_BASE_URL, CHAT_API_BASE, CHAT_AUTH_TOKEN } from "./webchatConfig";

/**
 * Get chat history/messages for a specific conversation
 * @param {string} conversationKey - The conversation key (e.g., "CONVO_user_01KBCP56TGJK3X46F_62b58b1d-4698-4d92-b29a-fe113c7b0310_1764583684")
 * @param {string} userId - The current user/agent ID
 * @param {string} companyUuid - The company UUID
 * @param {number} page - Page number for pagination (default: 1)
 * @returns {Promise<Object>} - Returns the chat messages
 */
export const getChatHistory = async (
  conversationKey,
  userId,
  companyUuid,
  page = 1
) => {
  try {
    console.log("📥 Fetching chat history:", {
      conversationKey,
      userId,
      companyUuid,
      page,
    });

    const url = `${CHAT_BASE_URL}/get-messages/${conversationKey}/${userId}/${companyUuid}?page=${page}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch chat history: ${response.status}`);
    }

    const responseData = await response.json();
    console.log("✅ Chat history fetched successfully:", responseData);

    // Normalize data structure for Usewebchat.js compatibility
    // If the API returns a direct array of messages, wrap it in an object
    let data = responseData;
    if (Array.isArray(responseData)) {
      data = { messages: responseData };
    }

    return { success: true, data };
  } catch (error) {
    console.error("❌ Error fetching chat history:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Close/end a chat conversation
 * @param {string} email - Agent email who is closing the conversation
 * @param {string} companyUuid - Company UUID
 * @param {string} wa_id - WhatsApp user ID
 * @param {string} conversationId - Conversation ID to close
 * @param {string} conversationName - Name shown to agent
 * @param {string} webhookId - Webhook ID
 * @param {string} userToken - User Token
 * @returns {Promise<Object>} - Returns the close chat response
 */
export const closeChat = async (
  email,
  companyUuid,
  wa_id,
  conversationId,
  conversationName,
  webhookId,
  userToken
) => {
  try {
    console.log("🔒 Closing chat:", {
      email,
      companyUuid,
      wa_id,
      conversationId,
      conversationName,
      webhookId,
      userToken,
    });

    // New URL structure: .../webchat/v2/wc-webhook/close-chat/{companyUuid}
    // CHAT_API_BASE is .../webchat/v2
    const url = `${CHAT_API_BASE}/wc-webhook/close-chat/${companyUuid}`;

    const payload = {
      wa_id,
      conversationId,
      conversationName,
      webhookId,
      userToken,
    };

    console.log("📤 Close chat payload:", payload);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to close chat: ${response.status}`);
    }

    const data = await response.json();
    console.log("✅ Chat closed successfully:", data);

    return { success: true, data };
  } catch (error) {
    console.error("❌ Error closing chat:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Helper function to format conversation key
 * @param {string} userId - User ID
 * @param {string} agentId - Agent ID
 * @param {string} companyUuid - Company UUID
 * @param {number} timestamp - Unix timestamp
 * @returns {string} - Formatted conversation key
 */
export const formatConversationKey = (
  userId,
  agentId,
  companyUuid,
  timestamp
) => {
  return `CONVO_${userId}_${agentId}_${timestamp}`;
};

/**
 * Parse conversation key to extract components
 * @param {string} conversationKey - The conversation key
 * @returns {Object} - Object containing userId, agentId, and timestamp
 */
export const parseConversationKey = (conversationKey) => {
  // Remove "CONVO_" prefix
  const key = conversationKey.replace("CONVO_", "");
  const parts = key.split("_");

  if (parts.length >= 3) {
    return {
      userId: parts[0],
      agentId: parts[1],
      timestamp: parts[parts.length - 1],
    };
  }

  return null;
};


/**
 * Add a title to a conversation
 */
export const addConversationTitle = async (conversationId, uuid, title) => {
  try {
    console.log("📝 Adding conversation title:", { conversationId, title });

    const response = await fetch(`${CHAT_API_BASE}/conversation/add-conversation-title`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${CHAT_AUTH_TOKEN}`
      },
      body: JSON.stringify({
        conversationId,
        uuid,
        conversationName: title
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to add conversation title");
    }

    console.log("✅ Conversation title added:", data);
    return { success: true, data };
  } catch (error) {
    console.error("❌ Error adding conversation title:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get conversation details including title
 */
export const getConversationTitle = async (conversationId, uuid) => {
  try {
    console.log("🔍 Getting conversation title for:", conversationId);

    const response = await fetch(`${CHAT_API_BASE}/conversation/get-conversation/${conversationId}/${uuid}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${CHAT_AUTH_TOKEN}`
      }
    });

    const data = await response.json();

    if (response.status === 404) {
      return { success: false, notFound: true, error: data.message || "Conversation title not found" };
    }

    if (!response.ok) {
      throw new Error(data.message || "Failed to get conversation title");
    }

    console.log("✅ Got conversation details:", data);
    return { success: true, data };
  } catch (error) {
    console.error("❌ Error getting conversation title:", error);
    return { success: false, error: error.message };
  }
}

export const chatHistoryAPI = {
  getChatHistory,
  closeChat,
  formatConversationKey,
  parseConversationKey,
  addConversationTitle,
  getConversationTitle
};
